import React, { useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Button from "Components/button/Button";
import DomainStatusBadge from "Components/domainStatusBadge/DomainStatusBadge";
import ResponsiveTable from "Components/responsiveTable/ResponsiveTable";
import FullPaginationControls from "Components/fullPaginationControls/FullPaginationControls";
import TranslatedString from "Components/TranslatedString";
import withConfirmationModal from "Components/withConfirmationModal";
import FirstView from "./FirstView";
import ArchiveDomainRow from "Components/archiveDomainRow/ArchiveDomainRow";
import qs from "query-string";
import { Icon, plus, archive } from "Icons/index";
import t from "Utils/translation";
import { MediaQueriesContext, AuthContext } from "../../App";
import { useApiQuery, useApiMutation } from "../../hooks";

import cx from "classnames";
import s from "./listArchive.scss";

const DOMAIN_STATUSES = ["activating", "disabling", "enabling", "deleting", "updating"];

const ArchiveDomainListScreen = ({ openConfirmationModal, openDisablingConfirmationModal }) => {
  const history = useHistory();
  const location = useLocation();
  const { isMobileDevice, isMediaMobile } = useContext(MediaQueriesContext);
  const { getLoggedUser, isLoggedUserAdmin } = useContext(AuthContext);

  const {
    data: archiveDomains = [],
    pagination: archiveDomainsPaginationInfo,
    isLoading: isArchiveDomainsLoading,
    isRefreshing: archiveDomainsIsRefreshing,
    getData: getArchiveDomains,
    updateData: updateArchiveDomains,
  } = useApiQuery("getArchiveDomains", {
    enablePagination: true,
    autoRefresh: {
      shouldRefresh: (domains) => domains.some(({ archive_status }) => DOMAIN_STATUSES.includes(archive_status)),
      interval: 4000,
    },
  });

  const { mutate: deleteDomain } = useApiMutation("deleteArchiveDomain", {
    optimistic: (domainCode) =>
      updateArchiveDomains((domains) =>
        domains.map((d) => (d.code === domainCode ? { ...d, archive_status: "deleting" } : d))
      ),
  });
  const { mutate: updateDomainStatus } = useApiMutation("editArchiveDomainStatus", {
    optimistic: (domainCode, status) =>
      updateArchiveDomains((domains) =>
        domains.map((d) => (d.code === domainCode ? { ...d, archive_status: status } : d))
      ),
  });

  const handleAddNew = () => {
    history.push({
      pathname: "/archive/new",
      state: {
        prevPagination: location.search,
      },
    });
  };

  const handleToggleStatus = async (domainCode, newStatus) => {
    try {
      await updateDomainStatus(domainCode, newStatus);
    } catch (error) {
      console.error("Error changing domain status:", error);
    }
  };

  const handleDeleteDomain = async (domainCode) => {
    try {
      await deleteDomain(domainCode);
    } catch (error) {
      console.error("Error deleting archive domain:", error);
    }
  };

  const queryParams = qs.parse(location.search);

  const currentUser = getLoggedUser();
  const canAddArchive = !(
    currentUser.type === "TeamMember" && !currentUser.permissions.includes("create_vault_email_accounts")
  );

  if (
    archiveDomains.length === 0 &&
    !queryParams.archive_status &&
    !queryParams.customer_code &&
    !isArchiveDomainsLoading &&
    !queryParams.query
  ) {
    const isCustomerWithoutPermissions =
      (currentUser.type === "Customer" || currentUser.type === "Manager") &&
      currentUser.max_email_accounts_archive === 0;
    return <FirstView canAddArchive={canAddArchive} disabled={isCustomerWithoutPermissions} location={location} />;
  }

  const tableHeaders = [
    {
      name: t("table.header.status"),
      size: isMobileDevice ? "2rem" : "4rem",
      center: true,
    },
    {
      name: t("table.header.domain"),
      key: "name",
    },
    {
      name: t("table.header.customer"),
      size: "10rem",
      hide: !isLoggedUserAdmin(),
      hideOnMobile: true,
    },
    {
      name: t("table.header.accounts"),
      size: isMobileDevice ? "3rem" : "5rem",
    },
    {
      size: "3rem",
      center: true,
    },
  ];

  return (
    <div className={s.listArchive}>
      <div className={s.headerList}>
        <div className={cx("sectionTitle", s.titleList)}>
          <TranslatedString>archive.list.header.title</TranslatedString>
        </div>

        {!isMediaMobile && (
          <div className={cx("sectionDesc", s.descList)}>
            <TranslatedString>archive.list.header.dsc</TranslatedString>
          </div>
        )}

        <div className={s.actionsList}>
          <Button docsKey="archive" color="docs" className={s.btnList} onlyIcon={isMediaMobile}>
            button.docs
          </Button>
          {canAddArchive && (
            <Button icon={plus} onClick={handleAddNew} onlyIcon={isMediaMobile}>
              button.addArchive
            </Button>
          )}
        </div>
      </div>
      <div className={s.contentList}>
        <FullPaginationControls
          className={s.filters}
          paginationInfo={archiveDomainsPaginationInfo}
          location={location}
          refreshList={() => getArchiveDomains()}
          listFilterParam="archive_status"
          listFilters={["all", "enabled", "disabled"]}
          listFilterLegends={{
            all: <Icon svg={archive} width="0.75rem" height="0.75rem" />,
            enabled: <DomainStatusBadge status="enabled" isHealthy showAsPreview />,
            disabled: <DomainStatusBadge status="disabled" isHealthy showAsPreview />,
          }}
          showCustomerSelection={isLoggedUserAdmin()}
          isLoading={isArchiveDomainsLoading}
          totals={archiveDomainsPaginationInfo?.totals}
        />

        <div className={s.table}>
          <ResponsiveTable
            history={history}
            areRowsLoading={isArchiveDomainsLoading && !archiveDomainsIsRefreshing}
            location={location}
            headers={tableHeaders}
            rows={archiveDomains.map((domain, index) => (
              <ArchiveDomainRow
                domain={domain}
                location={location}
                key={domain.code || index}
                isAdmin={isLoggedUserAdmin()}
                toggleStatus={(newStatus) => {
                  if (domain.archive_status === "disabled") {
                    handleToggleStatus(domain.code, newStatus);
                  } else {
                    openDisablingConfirmationModal(
                      domain,
                      (domainCode) => handleToggleStatus(domainCode, newStatus),
                      <TranslatedString subs={{ domain: domain.name }}>
                        modal.disable.archive.domain.text
                      </TranslatedString>
                    );
                  }
                }}
                deleteDomain={() =>
                  openConfirmationModal(
                    domain,
                    handleDeleteDomain,
                    <TranslatedString subs={{ domain: domain.name }}>modal.delete.archive.domain.text</TranslatedString>
                  )
                }
              />
            ))}
          />
        </div>
      </div>
    </div>
  );
};

export default withConfirmationModal(
  withConfirmationModal(ArchiveDomainListScreen, {
    title: <TranslatedString>modal.delete.archive.domain.title</TranslatedString>,
    buttonLabel: "button.delete",
  }),
  {
    title: <TranslatedString>modal.disable.archive.domain.title</TranslatedString>,
    name: "DisablingConfirmation",
    buttonLabel: "button.disable",
  }
);
